import { allProjects, usersById, allClients, ACTIVE_STATUSES, asDate, updateProjectFields, leadRefPath, addRecordChange, cascadeVoidToEnquiry, teamTasks, createTask, listInstructions, projectEmails } from '../db.js';
import { openEndingConfirm } from './ending-confirm.js';
import { schemeOf, escapeHtml, refTail, titleCase, initials, skeleton, sentenceLabel, safeUrl, CLIENT_SCHEMES } from '../ui.js';
import { summaryMarkerHtml, hasSiteWarnings } from './site-checks-surfaces.js';
import { navIcon } from '../nav-icons.js';
import { newestFirst } from '../mail-index.js';
import { goToView } from './calc-icon.js';
import { nextActionFor } from './projects-next.js';
import { openStagePicker, stageText, openProjectMenu, requestLogTime } from './project-menus.js';
import { openNewTask } from './task-new.js';
import { teamFrom, meIn, dayKey } from '../tasks.js';
import { openMeeting, resumableMeeting, followableMeeting } from './meeting.js';
import { projectComparator, nextSort, isDefaultSort, readSort, writeSort, DEFAULT_SORT } from './projects-sort.js';

// Statuses in lifecycle order — inline edits (like the detail screen) only ever
// move a project FORWARD (design law D2).
const WORK_ORDER = ['Not Started', 'In Progress', 'Review', 'Ready to Issue', 'Part Issued', 'Issued', 'Complete'];
const REVIEWER_ROLES = ['Owner', 'Director', 'Finance', 'Coordinator'];

// Lightweight anchored dropdown for the inline status/lead pickers. One open at
// a time; closes on outside-click or Esc.
let openPick = null;
function closePick() {
  if (!openPick) return;
  openPick.remove(); openPick = null;
  document.removeEventListener('click', pickOutside, true);
  document.removeEventListener('keydown', pickKey, true);
}
function pickOutside(ev) { if (openPick && !openPick.contains(ev.target)) closePick(); }
function pickKey(ev) { if (ev.key === 'Escape') closePick(); }
function openInlinePicker(anchor, items, currentKey, onPick) {
  closePick();
  const menu = document.createElement('div');
  menu.className = 'inline-pick';
  menu.innerHTML = items.map((it) =>
    `<button type="button" class="inline-pick-opt${String(it.key) === String(currentKey) ? ' current' : ''}" data-k="${escapeHtml(String(it.key))}">${escapeHtml(it.label)}</button>`).join('');
  document.body.appendChild(menu);
  const r = anchor.getBoundingClientRect();
  const mw = menu.offsetWidth, mh = menu.offsetHeight;
  let left = Math.min(r.left, window.innerWidth - mw - 8);
  let top = r.bottom + 4;
  if (top + mh > window.innerHeight - 8) top = Math.max(8, r.top - mh - 4);
  menu.style.left = Math.max(8, left) + 'px';
  menu.style.top = top + 'px';
  menu.querySelectorAll('.inline-pick-opt').forEach((b) =>
    b.addEventListener('click', (ev) => { ev.stopPropagation(); closePick(); onPick(b.dataset.k); }));
  openPick = menu;
  setTimeout(() => {
    document.addEventListener('click', pickOutside, true);
    document.addEventListener('keydown', pickKey, true);
  }, 0);
}

// Tiny transient toast for save errors.
function toast(msg) {
  const t = document.createElement('div');
  t.className = 'inline-toast'; t.textContent = msg;
  document.body.appendChild(t);
  setTimeout(() => t.remove(), 3200);
}

// PROJECTS — v4.0.0 (Karan's design export 22/10/2026, p05;
// docs/ui-refresh/01-home-workspace.md §2).
//
// The title and one meta line, a proportional bar of the live stages, one row
// of controls (Mine | Live | Not started | Complete, Group, Filter, search),
// then the table: number, project (client over description), stage, the next
// action, lead, due. A click selects a row and shows it in the preview panel on
// the right; Enter or a double-click opens it; J and K move. Under 1100px there
// is no room for the panel, so a click opens the project as it always did.
//
// MEETING MODE is its own screen now (views/meeting.js, v4.0.0 design 23/10).
// Every value renders through escapeHtml (house rule 0); every figure and the
// next action are DERIVED — nothing new is stored.


const two = (n) => String(n).padStart(2, '0');

// The live stages, in the order the bar draws them (design p05). Not started and
// Complete are not live, so they are not in the bar.
const STAGE_BAR = [
  { key: 'In Progress', label: 'In progress', cls: 'prog' },
  { key: 'Review', label: 'Review', cls: 'review' },
  { key: 'Ready to Issue', label: 'Ready to issue', cls: 'ready' },
  { key: 'Part Issued', label: 'Part issued', cls: 'part' },
];
// Stage pill colours step darker as work moves forward (p85) — the same classes
// ui.js pill() picks, so a stage reads the same on every screen.
const STAGE_CLS = {
  'Not Started': 'p-grey-cool', 'In Progress': 'p-blue', Review: 'p-blue2', 'Ready to Issue': 'p-navy',
  'Part Issued': 'p-grey', Issued: 'p-ink', Complete: 'p-ok', 'On Hold': 'p-grey', Void: 'p-grey',
};
const stageLabel = (s) => sentenceLabel(s || 'Not Started') || '—';

export async function renderProjects(mount, { onOpen, leadFilter = null, leadName = '', session = null, onBringIn = null } = {}) {
  const canEdit = !!(session && Array.isArray(session.roles) && session.roles.some((r) => REVIEWER_ROLES.includes(r)));
  // Contractor go-live D: the Owner and Office bring in projects that started before Track (app.js passes onBringIn only for a contractor).
  const canBringIn = !!(onBringIn && session && Array.isArray(session.roles) && session.roles.some((r) => r === 'Owner' || r === 'Office'));
  const meId = String((session && session.email) || '').split('@')[0].toLowerCase();

  mount.innerHTML = `<div>${skeleton({ band: false })}</div>`;

  let projects = [], users = {}, clients = [], tasks = [];
  try {
    [projects, users, clients, tasks] = await Promise.all([
      allProjects(), usersById().catch(() => ({})), allClients().catch(() => []),
      // Open tasks feed the Next action column — the same read Home makes.
      teamTasks().catch(() => []),
    ]);
    projects = projects.filter((p) => !p.isTest); // sandbox test projects stay out of the live list
    // Bug fix (Karan 10/07): some projects synced without a denormalised clientName;
    // resolve it from the client the project links to so the list never shows "—".
    const clientById = Object.fromEntries((clients || []).map((c) => [c.id, c.name]));
    projects.forEach((p) => { if (!p.clientName && p.clientRef) p.clientName = clientById[refTail(p.clientRef)] || p.clientName; });
  } catch (e) {
    mount.innerHTML = `<div class="load-error"><div class="empty-title">Projects could not be read</div><p>${escapeHtml(e && e.message || e)}</p></div>`;
    return;
  }
  const clientDoc = Object.fromEntries((clients || []).map((c) => [c.id, c]));
  const tasksByProject = {};
  for (const t of tasks || []) if (t && t.projectId) (tasksByProject[t.projectId] ||= []).push(t);

  const leadNameOf = (p) => {
    const id = refTail(p.lead);
    return id ? titleCase((users[id] && users[id].displayName) || id) : '';
  };
  const firstName = (id) => { const u = users[id]; return titleCase(String((u && u.displayName) || id || '').split(' ')[0]); };

  // "Overdue" is a RULE, never a stored flag: an open project (not Issued/
  // Complete) whose due date — set on the project in V2 — has passed. Matches the
  // project-detail deadline rule (Issued/Complete are never late).
  const todayStart = new Date(); todayStart.setHours(0, 0, 0, 0);
  // Open = live, not started or on hold. Not "anything but Complete": 452 projects
  // from 2022–25 carry no status at all, and Void is closed, so the old rule
  // told the brief 646 were open beside a 137-live headline (Karan 21/10).
  const isOpen = (p) => ACTIVE_STATUSES.includes(p.status) || p.status === 'Not Started' || p.status === 'On Hold';
  const dueOf = (p) => { const d = p.due ? asDate(p.due) || new Date(p.due) : null; return d && !isNaN(d) ? d : null; };
  const isOverdue = (p) => {
    if (!isOpen(p)) return false;
    const d = dueOf(p);
    return !!d && d < todayStart;
  };
  const daysOver = (p) => {
    if (!isOverdue(p)) return 0;
    return Math.max(1, Math.floor((todayStart - dueOf(p)) / 86400000));
  };
  const ddmm = (d) => (d ? `${two(d.getDate())}/${two(d.getMonth() + 1)}` : '—');
  const nextOf = (p) => nextActionFor(p, tasksByProject[p.id] || [], { today: todayStart, overdue: isOverdue(p), firstName });

  // ---- Inline editing (reviewers only) ------------------------------------
  // Writes go straight to Firestore (V2 is the write-master as of 13/07); the
  // local object + the cell update optimistically and revert on error.
  const leadItems = [{ key: '', label: 'Unassigned' }].concat(
    Object.values(users)
      .sort((a, b) => String(a.displayName || a.id).localeCompare(String(b.displayName || b.id)))
      .map((u) => ({ key: u.id, label: titleCase(u.displayName || u.id) })));

  // On hold / Void are ENDINGS — they alter others' work and need a reason, so
  // they open the shared confirm (Karan 17/08 — usable from Meeting mode too),
  // never a silent one-tap. Returns a Promise that resolves after the write.
  function applyEnding(p, kind, after) {
    const prev = p.status;
    const isVoid = kind === 'Void';
    return new Promise((resolve) => {
      openEndingConfirm({
        eyebrow: isVoid ? 'Void — never started' : 'Put on hold',
        record: `${p.number || p.id} — ${p.title || p.clientName || ''}`,
        whatStays: isVoid
          ? ['The project number — numbers are never reused', 'The register entry and its history', 'A Lost enquiry holding the client, scope and quote — reopen it if the work comes back']
          : ['Everything on the project', 'Time already logged (flagged for review)', 'It stays searchable'],
        whatChanges: isVoid
          ? 'Marks a project that never actually happened. It leaves the live list for good. If work WAS done, use Complete instead.'
          : 'Drops out of the live list until work resumes. Comes off hold by itself when time is logged or an instruction is added.',
        field: { label: isVoid ? 'Why it never started' : 'Why it’s on hold', type: 'text', placeholder: isVoid ? 'e.g. enquiry withdrawn before instruction' : 'e.g. awaiting client sign-off', value: '' },
        confirmLabel: isVoid ? 'Mark as Void' : 'Put on hold', reversibleNote: 'Reversible by setting a status again',
        onConfirm: async (reason) => {
          const at = new Date().toISOString();
          const to = isVoid ? 'Void' : 'On Hold';
          p.status = to;
          await updateProjectFields(p.id, { status: to, statusNote: reason || null,
            ...(to === 'On Hold' ? { heldReason: reason || null, statusBeforeHold: prev } : {}),
            statusUpdatedAt: at, statusSource: `manual (${session.email})`, updatedBy: session.email, updatedAt: at });
          await addRecordChange({ record: 'projects/' + p.id, field: 'status', label: 'Status', from: prev, to, reason: reason || null, by: session.email, byName: session.displayName, at, money: false });
          // Void cascade (Karan 17/08): the dead knowledge moves back to the
          // pipeline — the linked enquiry flips to Lost (or one is created for
          // pre-V2 projects) so the job is revivable from Enquiries. The Void
          // itself has already landed, so a cascade failure is a toast, not a
          // rollback.
          if (isVoid) {
            try { await cascadeVoidToEnquiry(p, reason, session); }
            catch (e) { toast('Voided, but the enquiry record could not be updated — ' + (e.message || e)); }
          }
          if (after) after();
          resolve();
        },
      });
    });
  }
  async function applyStatus(p, next) {
    if (!next || next === p.status) return;
    if (next === 'On Hold' || next === 'Void') return applyEnding(p, next === 'Void' ? 'Void' : 'On Hold');
    const prev = p.status;
    const at = new Date().toISOString();
    p.status = next;
    try {
      await updateProjectFields(p.id, {
        status: next, statusUpdatedAt: at,
        statusSource: `manual (${session.email})`, updatedBy: session.email, updatedAt: at,
      });
      addRecordChange({ record: 'projects/' + p.id, field: 'status', label: 'Status', from: prev || null, to: next, by: session.email, byName: session.displayName || null, at, money: false }).catch(() => {});
    } catch (e) { p.status = prev; toast('Could not update the stage — ' + (e.message || e)); throw e; }
  }
  async function applyLead(p, userId) {
    const path = leadRefPath(userId) || null;
    if ((path || null) === (p.lead || null)) return;
    const prev = p.lead;
    p.lead = path;
    try {
      await updateProjectFields(p.id, { lead: path, updatedBy: session.email, updatedAt: new Date().toISOString() });
    } catch (e) { p.lead = prev; toast('Could not reassign lead — ' + (e.message || e)); throw e; }
  }
  // THE STAGE PICKER MOVES FORWARD ONLY (design law D2). The design draws the
  // pill as freely editable; the picker (§11, project-menus.js) disables every
  // stage behind the current one. Endings (On hold) live in the row menu.
  const wireStatusCell = (cell, p, after) => {
    cell.classList.add('cell-edit');
    cell.addEventListener('click', (ev) => {
      ev.stopPropagation();
      openStagePicker(cell, {
        number: p.number, status: p.status,
        onPick: (k) => applyStatus(p, k).then(() => { toast(`${p.number || 'Project'} moved to ${stageText(k)}`); if (after) after(); }).catch(() => {}),
      });
    });
  };

  // ---- §12 The row menu: ⋯ at the row's end, or a right-click on the row ---
  // Six items at most (row-menu law): open, log time, add a task, change lead,
  // the folder (or, with none on file, the link), and the ending last in navy.
  const people = () => teamFrom(Object.entries(users || {}).map(([id, u]) => ({ id, ...u })), [], dayKey());
  const addTaskFor = (p) => openNewTask({
    projects: [{ id: p.id, number: p.number || '', name: p.clientName || '', site: (p.siteAddress && p.siteAddress.line1) || '', status: p.status || '' }],
    project: { id: p.id }, people: people(), today: dayKey(), me: meIn(people(), session && session.email),
    onCreate: async (fields) => { await createTask(fields, session); toast(`Task added to ${p.number || 'the project'}`); },
  });
  const copyLink = (p) => {
    const url = `${location.origin}/projects/${encodeURIComponent(p.number || p.id)}`;
    (navigator.clipboard ? navigator.clipboard.writeText(url) : Promise.reject()).then(() => toast('Link copied'), () => toast(url));
  };
  const rowMenuItems = (p, rowEl) => {
    const folder = p.folderUrl || (p.folderId ? `https://drive.google.com/drive/folders/${encodeURIComponent(p.folderId)}` : '');
    return [
      { label: 'Open project', hint: 'Enter', onClick: () => open(p) },
      { label: 'Log time', onClick: () => requestLogTime({ projectId: p.id, projectNo: p.number || null }, () => goToView('time')) },
      { label: 'Add a task', onClick: () => addTaskFor(p) },
      { label: 'Change lead…', role: canEdit ? '' : 'Reviewer', allowed: canEdit,
        onClick: () => { const ld = rowEl && rowEl.querySelector('.pj4-lead'); if (ld) ld.click(); } },
      folder
        ? { label: 'Open Drive folder', sep: true, onClick: () => window.open(safeUrl(folder), '_blank', 'noopener') }
        : { label: 'Copy link', sep: true, onClick: () => copyLink(p) },
      { label: 'Put on hold', sep: true, ending: true, role: canEdit ? '' : 'Reviewer', allowed: canEdit && isOpen(p) && p.status !== 'On Hold',
        onClick: () => applyEnding(p, 'On Hold', () => draw()) },
    ];
  };

  // ---- Counts (the whole portfolio, or one lead's slice from a person search)
  const scoped = leadFilter ? projects.filter((p) => refTail(p.lead) === leadFilter) : projects;
  const cnt = (pred) => scoped.filter(pred).length;
  const isLive = (p) => ACTIVE_STATUSES.includes(p.status);
  const isDone = (p) => p.status === 'Complete' || p.status === 'Issued';
  const isMine = (p) => isLive(p) && !!meId && String(refTail(p.lead) || '').toLowerCase() === meId;
  const liveCount = cnt(isLive);
  const lateCount = cnt((p) => isLive(p) && isOverdue(p));
  const counts = { Mine: cnt(isMine), Live: liveCount, 'Not Started': cnt((p) => p.status === 'Not Started'), Complete: cnt(isDone), All: scoped.length };
  const stageCounts = Object.fromEntries(STAGE_BAR.map((s) => [s.key, cnt((p) => p.status === s.key)]));
  // "Has site warnings" (hand-off §6): read from the denormalised summary only —
  // no per-row reads. The field is absent until the server flag is on, so the
  // count is 0 and the option is not offered.
  const siteWarnCount = cnt((p) => hasSiteWarnings(p.siteChecksSummary));

  // Scheme options are limited to the real scheme types (Karan 17/07) — a junk
  // value in a project's scheme field (e.g. a client abbreviation like "HSD")
  // must NOT become its own option. ADIPS is a SERVICE, offered on its own.
  const VALID_SCHEMES = CLIENT_SCHEMES;   // the org's schemes (org.js fills it from settings)
  const schemes = [...new Set(projects.map((p) => {
    const raw = String(p.scheme || '').trim().toLowerCase();
    return VALID_SCHEMES.find((v) => v.toLowerCase() === raw) || null;
  }).filter(Boolean))].sort();
  const hasAdips = projects.some((p) => String(p.service || '').toUpperCase() === 'ADIPS');
  const schemeDefs = [{ key: 'All', label: 'All schemes' }]
    .concat(hasAdips ? [{ key: 'ADIPS', label: 'ADIPS' }] : [])
    .concat(schemes.map((s) => ({ key: s, label: titleCase(s) })));
  const leadIds = [...new Set(projects.map((p) => refTail(p.lead)).filter(Boolean))]
    .sort((a, b) => leadNameOfId(a).localeCompare(leadNameOfId(b)));
  function leadNameOfId(id) { return titleCase((users[id] && users[id].displayName) || id); }

  // ---- Controls state -----------------------------------------------------
  let view = leadFilter ? 'All' : 'Live';
  let activeScheme = 'All';
  let activeLead = leadFilter || 'All';
  // The order (v4.23.9): newest first unless this person picked a column header.
  const sortStore = (() => { try { return window.localStorage; } catch (_) { return null; } })();
  let sort = readSort(sortStore, meId);
  let groupBy = 'none';
  let siteOnly = false;
  let q = '';
  let selId = null;
  let shown = [];
  const wide = () => window.innerWidth > 1100;

  const GROUPS = { none: 'none', stage: 'stage', lead: 'lead', client: 'client', scheme: 'scheme' };
  const groupKeyOf = (p) => groupBy === 'stage' ? stageLabel(p.status)
    : groupBy === 'lead' ? (leadNameOf(p) || 'Unassigned')
    : groupBy === 'client' ? (p.clientName || '—')
    : groupBy === 'scheme' ? (schemeOf(p.scheme) ? titleCase(schemeOf(p.scheme)) : 'No scheme') : '';

  // ---- Screen shell -------------------------------------------------------
  const meta = [`${liveCount} live`, lateCount ? `${lateCount} late` : ''].filter(Boolean).join(' · ')
    + (leadFilter ? ` · lead: ${leadName || titleCase(leadFilter)}` : '');
  const barTotal = STAGE_BAR.reduce((n, s) => n + stageCounts[s.key], 0);
  const barHtml = barTotal ? `<div class="pj4-bar" role="img" aria-label="Live projects by stage: ${escapeHtml(STAGE_BAR.filter((s) => stageCounts[s.key]).map((s) => `${stageCounts[s.key]} ${s.label.toLowerCase()}`).join(', '))}">
      ${STAGE_BAR.filter((s) => stageCounts[s.key]).map((s) => `<div class="pj4-bar-seg" style="flex-grow:${stageCounts[s.key]}">
        <span class="pj4-bar-fill ${s.cls} draws"></span>
        <span class="pj4-bar-l"><span class="pj4-bar-n">${stageCounts[s.key]}</span> ${escapeHtml(s.label)}</span>
      </div>`).join('')}
    </div>` : '';
  const segDefs = [
    { key: 'Mine', label: 'Mine' }, { key: 'Live', label: 'Live' },
    { key: 'Not Started', label: 'Not started' }, { key: 'Complete', label: 'Complete' },
  ];
  const isMac = /Mac/.test(navigator.platform);

  mount.innerHTML = `
    <div class="pj4">
      <div class="pj4-main">
        <header class="pj4-head arrive-head">
          <div class="page-h"><h1 class="ct4-page-title">Projects</h1><span class="page-sub">${escapeHtml(meta)}</span></div>
          <div class="pj4-head-acts">
            ${canBringIn ? '<button id="pj4-bring-in" type="button" class="btn" title="A job that started before Track: keep its number, client and money so far">Bring in a project</button>' : ''}
            <button id="meeting" type="button" class="btn">Meeting mode</button>
          </div>
        </header>
        ${barHtml}
        <div class="pj4-controls">
          <div class="seg pj4-seg" id="pj4-seg" role="tablist" aria-label="Which projects"></div>
          <button type="button" class="btn pj4-drop" id="pj4-group" aria-haspopup="menu"><span id="pj4-group-l">Group: none</span>${navIcon('chevronDown', 14)}</button>
          <button type="button" class="btn pj4-drop" id="pj4-sort-reset" title="Back to newest first" hidden>Newest first</button>
          <button type="button" class="btn pj4-drop" id="pj4-filter" aria-haspopup="menu"><span id="pj4-filter-l">Filter</span>${navIcon('chevronDown', 14)}</button>
          <label class="pj4-search">${navIcon('search', 14)}<input id="search" type="search" autocomplete="off" placeholder="Number, client or description" aria-label="Search projects"></label>
        </div>
        <div id="rows-wrap" class="pj4-table" role="grid" aria-label="Projects"></div>
        <p class="pj4-foot" id="pj4-foot"></p>
      </div>
      <aside class="pj4-panel" id="pj4-panel" aria-label="Project preview" hidden></aside>
    </div>`;

  const root = mount.querySelector('.pj4');
  mount.querySelector('#pj4-bring-in')?.addEventListener('click', () => onBringIn && onBringIn());
  const rowsWrap = mount.querySelector('#rows-wrap');
  const segEl = mount.querySelector('#pj4-seg');
  const panel = mount.querySelector('#pj4-panel');
  const foot = mount.querySelector('#pj4-foot');

  function renderSeg() {
    segEl.innerHTML = segDefs.map((d) => `<button type="button" role="tab" data-k="${escapeHtml(d.key)}" class="${d.key === view ? 'active' : ''}" aria-selected="${d.key === view}">${escapeHtml(d.label)}<span class="n">${counts[d.key]}</span></button>`).join('');
  }
  segEl.addEventListener('click', (ev) => {
    const b = ev.target.closest('button[data-k]'); if (!b) return;
    view = b.dataset.k; renderSeg(); renderFilterLabel(); draw();
  });
  renderSeg();

  // ---- Menus: Group and Filter ---------------------------------------------
  // Filter holds what the old chip rows and selects did: scheme, lead, site
  // warnings — and "Every project", the old All tab.
  let menuEl = null;
  const closeMenu = () => { if (menuEl) { menuEl.remove(); menuEl = null; document.removeEventListener('click', menuOutside, true); document.removeEventListener('keydown', menuKey, true); } };
  function menuOutside(ev) { if (menuEl && !menuEl.contains(ev.target) && !ev.target.closest('.pj4-drop')) closeMenu(); }
  function menuKey(ev) { if (ev.key === 'Escape') { ev.stopPropagation(); closeMenu(); } }
  function openMenu(anchor, html, onPick) {
    const was = menuEl && menuEl.dataset.for === anchor.id;
    closeMenu();
    if (was) return;
    menuEl = document.createElement('div');
    menuEl.className = 'menu-v4 pj4-menu';
    menuEl.dataset.for = anchor.id;
    menuEl.setAttribute('role', 'menu');
    menuEl.innerHTML = html;
    document.body.appendChild(menuEl);
    const r = anchor.getBoundingClientRect();
    const mw = menuEl.offsetWidth, mh = menuEl.offsetHeight;
    menuEl.style.left = `${Math.max(8, Math.min(r.left, window.innerWidth - mw - 8))}px`;
    menuEl.style.top = `${r.bottom + 4 + mh > window.innerHeight - 8 ? Math.max(8, r.top - mh - 4) : r.bottom + 4}px`;
    menuEl.addEventListener('click', (ev) => {
      const b = ev.target.closest('button[data-act]'); if (!b) return;
      onPick(b.dataset.act, b.dataset.v);
    });
    menuEl.querySelector('button')?.focus();
    setTimeout(() => { document.addEventListener('click', menuOutside, true); document.addEventListener('keydown', menuKey, true); }, 0);
  }
  const opt = (act, v, label, on) => `<button type="button" role="menuitemradio" aria-checked="${on}" data-act="${act}" data-v="${escapeHtml(v)}" class="${on ? 'on' : ''}">${escapeHtml(label)}</button>`;
  const groupBtn = mount.querySelector('#pj4-group');
  groupBtn.addEventListener('click', () => {
    const labels = { none: 'None', stage: 'Stage', lead: 'Lead', client: 'Client', scheme: 'Scheme' };
    openMenu(groupBtn, Object.keys(GROUPS).map((k) => opt('group', k, labels[k], groupBy === k)).join(''), (act, v) => {
      groupBy = v; closeMenu();
      mount.querySelector('#pj4-group-l').textContent = `Group: ${labels[v].toLowerCase()}`;
      draw();
    });
  });
  function renderFilterLabel() {
    const n = (activeScheme !== 'All') + (activeLead !== 'All' && !leadFilter) + siteOnly + (view === 'All');
    const btn = mount.querySelector('#pj4-filter');
    mount.querySelector('#pj4-filter-l').textContent = n ? `Filter · ${n}` : 'Filter';
    btn.classList.toggle('on', !!n);
  }
  const filterBtn = mount.querySelector('#pj4-filter');
  filterBtn.addEventListener('click', () => {
    const html = `
      ${opt('view', 'All', `Every project (${counts.All})`, view === 'All')}
      <hr><div class="pj4-menu-h">Scheme</div>${schemeDefs.map((s) => opt('scheme', s.key, s.label, activeScheme === s.key)).join('')}
      <hr><div class="pj4-menu-h">Lead</div>${opt('lead', 'All', 'All leads', activeLead === 'All')}${leadIds.map((id) => opt('lead', id, leadNameOfId(id), activeLead === id)).join('')}
      ${siteWarnCount ? `<hr>${opt('site', '1', `Has site warnings (${siteWarnCount})`, siteOnly)}` : ''}`;
    openMenu(filterBtn, html, (act, v) => {
      if (act === 'view') { view = view === 'All' ? 'Live' : 'All'; renderSeg(); }
      if (act === 'scheme') activeScheme = v;
      if (act === 'lead') activeLead = v;
      if (act === 'site') siteOnly = !siteOnly;
      closeMenu(); renderFilterLabel(); draw();
    });
  });
  renderFilterLabel();

  mount.querySelector('#search').addEventListener('input', (ev) => { q = ev.target.value; draw(); });

  // "New project" has no create-flow in the repo — new work enters through a
  // won enquiry (design law D1). Surface that instead of a dead control.

  // ---- Filtering + table draw --------------------------------------------
  function filtered() {
    const needle = q.trim().toLowerCase();
    return projects.filter((p) => {
      if (view === 'Mine' && !isMine(p)) return false;
      if (view === 'Live' && !isLive(p)) return false;
      if (view === 'Not Started' && p.status !== 'Not Started') return false;
      if (view === 'Complete' && !isDone(p)) return false;
      if (activeScheme === 'ADIPS') { if (String(p.service || '').toUpperCase() !== 'ADIPS') return false; }
      else if (activeScheme !== 'All' && schemeOf(p.scheme) !== activeScheme) return false;
      if (activeLead !== 'All' && refTail(p.lead) !== activeLead) return false;
      if (siteOnly && !hasSiteWarnings(p.siteChecksSummary)) return false;
      if (!needle) return true;
      return [p.number, p.clientName, p.title, p.status, leadNameOf(p)]
        .some((v) => String(v || '').toLowerCase().includes(needle));
    }).sort(projectComparator(sort, { leadName: leadNameOf, dueOf }));
  }

  const stageHtml = (p) => {
    const cls = STAGE_CLS[p.status] || 'p-grey-cool';
    return canEdit
      ? `<button type="button" class="pill ${cls} pj4-stage" aria-label="Stage: ${escapeHtml(stageLabel(p.status))}. Move it on">${escapeHtml(stageLabel(p.status))}${navIcon('chevronDown', 12)}</button>`
      : `<span class="pill ${cls} pj4-stage">${escapeHtml(stageLabel(p.status))}</span>`;
  };
  const leadHtml = (p) => {
    const name = leadNameOf(p);
    const inner = name ? `<span class="av off pj4-av">${escapeHtml(initials(name))}</span>` : '<span class="pj4-none">—</span>';
    return canEdit
      ? `<button type="button" class="pj4-lead" title="${escapeHtml(name ? `Lead: ${name}` : 'No lead')}" aria-label="${escapeHtml(name ? `Lead ${name}. Change` : 'Assign a lead')}">${inner}</button>`
      : `<span class="pj4-lead" title="${escapeHtml(name ? `Lead: ${name}` : 'No lead')}">${inner}</span>`;
  };
  const rowHtml = (p) => {
    const late = isOverdue(p);
    const nx = nextOf(p);
    const d = dueOf(p);
    return `<div class="pj4-row${p.id === selId ? ' sel' : ''}" data-id="${escapeHtml(p.id)}" role="row" aria-selected="${p.id === selId}" tabindex="-1">
      <span class="pj4-no" role="gridcell">${escapeHtml(p.number || '—')}</span>
      <span class="pj4-proj" role="gridcell"><span class="pj4-client">${escapeHtml(p.clientName || '—')}${summaryMarkerHtml(p.siteChecksSummary)}</span><span class="pj4-desc">${escapeHtml(p.title || p.service || '')}</span></span>
      <span class="pj4-stage-cell" role="gridcell">${stageHtml(p)}</span>
      <span class="pj4-next${nx && nx.late ? ' late-text strong' : ''}" role="gridcell">${escapeHtml(nx ? nx.text : '—')}</span>
      <span class="pj4-lead-cell" role="gridcell">${leadHtml(p)}</span>
      <span class="pj4-due${late ? ' late-text strong' : ''}" role="gridcell">${escapeHtml(d ? ddmm(d) : '—')}</span>
      <button type="button" class="pj4-dots" aria-label="Actions for ${escapeHtml(p.number || 'this project')}" aria-haspopup="menu">${navIcon('more', 16)}</button>
    </div>`;
  };
  // Sortable headers (No., Project = client, Stage, Lead, Due); Next action is not a sort.
  const sortHead = (key, label, cls = '') => {
    const on = sort.key === key;
    const ariaSort = on ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none';
    return `<span role="columnheader" aria-sort="${ariaSort}"${cls ? ` class="${cls}"` : ''}><button type="button" class="pj4-sort${on ? ' on' : ''}" data-sort="${key}" title="Sort by ${escapeHtml(label.toLowerCase())}">${escapeHtml(label)}${on ? `<span class="pj4-sort-arrow" aria-hidden="true">${sort.dir === 'asc' ? '↑' : '↓'}</span>` : ''}</button></span>`;
  };
  const headHtml = () => `<div class="pj4-row pj4-thead reg-head" role="row">
      ${sortHead('number', 'No.')}${sortHead('client', 'Project')}${sortHead('stage', 'Stage')}<span role="columnheader">Next action</span>${sortHead('lead', 'Lead')}${sortHead('due', 'Due', 'r')}<span></span>
    </div>`;
  const sortReset = mount.querySelector('#pj4-sort-reset');
  const setSort = (next) => { sort = next; writeSort(sortStore, meId, sort); sortReset.hidden = isDefaultSort(sort); draw(); };
  sortReset.hidden = isDefaultSort(sort);
  sortReset.addEventListener('click', () => setSort({ ...DEFAULT_SORT }));

  function draw() {
    closePick();
    shown = filtered();
    if (selId && !shown.some((p) => p.id === selId)) selId = null;
    if (!selId && wide() && shown.length) selId = shown[0].id;

    if (!shown.length) {
      rowsWrap.innerHTML = headHtml() + `<div class="empty-state"><div class="empty-title">No matching projects</div><p>${q ? 'Nothing matches that search.' : 'Nothing in this view.'} Try another tab, or clear the filter.</p></div>`;
    } else if (groupBy === 'none') {
      rowsWrap.innerHTML = headHtml() + `<div class="pj4-body arrive">${shown.map(rowHtml).join('')}</div>`;
    } else {
      const groups = new Map();
      for (const p of shown) { const k = groupKeyOf(p); if (!groups.has(k)) groups.set(k, []); groups.get(k).push(p); }
      // Group order: stages in lifecycle order, everything else A–Z.
      const keys = [...groups.keys()].sort((a, b) => groupBy === 'stage'
        ? WORK_ORDER.map(stageLabel).indexOf(a) - WORK_ORDER.map(stageLabel).indexOf(b)
        : a.localeCompare(b));
      shown = keys.flatMap((k) => groups.get(k));
      rowsWrap.innerHTML = headHtml() + `<div class="pj4-body">${keys.map((k) => `<div class="pj4-group" role="row"><span>${escapeHtml(k)}</span><span class="pj4-group-n">${groups.get(k).length}</span></div>${groups.get(k).map(rowHtml).join('')}`).join('')}</div>`;
    }
    foot.innerHTML = `${shown.length.toLocaleString('en-GB')} of ${projects.length.toLocaleString('en-GB')}<span class="pj4-keys"> · <kbd>J</kbd> <kbd>K</kbd> to move, <kbd>Enter</kbd> to open</span>`;

    const byId = new Map(shown.map((p) => [p.id, p]));
    rowsWrap.querySelectorAll('.pj4-row[data-id]').forEach((row) => {
      const p = byId.get(row.dataset.id);
      if (!p || !canEdit) return;
      // Inline stage/lead pickers (reviewers only). Each picker stopPropagation()s
      // so it never also selects or opens the row.
      const st = row.querySelector('.pj4-stage');
      const ld = row.querySelector('.pj4-lead');
      if (st) wireStatusCell(st, p, () => { draw(); paintPanel(); });
      if (ld) ld.addEventListener('click', (ev) => {
        ev.stopPropagation();
        openInlinePicker(ld, leadItems, refTail(p.lead) || '', (k) => applyLead(p, k).then(() => { draw(); paintPanel(); }).catch(() => {}));
      });
    });
    paintPanel();
  }

  // ---- Selection, the preview panel and the keyboard -----------------------
  const open = (p) => { if (p && onOpen) onOpen(p.id, p.number); };
  function select(id, { scroll = false } = {}) {
    selId = id;
    rowsWrap.querySelectorAll('.pj4-row[data-id]').forEach((r) => {
      const on = r.dataset.id === id;
      r.classList.toggle('sel', on);
      r.setAttribute('aria-selected', String(on));
      if (on && scroll) r.scrollIntoView({ block: 'nearest' });
    });
    paintPanel();
  }
  rowsWrap.addEventListener('click', (ev) => {
    const sb = ev.target.closest('.pj4-sort[data-sort]');
    if (sb) { setSort(nextSort(sort, sb.dataset.sort)); rowsWrap.querySelector(`.pj4-sort[data-sort="${sb.dataset.sort}"]`)?.focus(); return; }
    const row = ev.target.closest('.pj4-row[data-id]'); if (!row) return;
    const p = shown.find((x) => x.id === row.dataset.id); if (!p) return;
    const dots = ev.target.closest('.pj4-dots');
    if (dots) { ev.stopPropagation(); if (wide()) select(p.id); openProjectMenu({ anchor: dots, align: 'right' }, rowMenuItems(p, row)); return; }
    if (!wide()) { open(p); return; }   // no room for the panel: a click opens, as before
    select(p.id);
  });
  rowsWrap.addEventListener('contextmenu', (ev) => {
    const row = ev.target.closest('.pj4-row[data-id]'); if (!row) return;
    const p = shown.find((x) => x.id === row.dataset.id); if (!p) return;
    ev.preventDefault();
    if (wide()) select(p.id);
    openProjectMenu({ x: ev.clientX, y: ev.clientY }, rowMenuItems(p, row));
  });
  rowsWrap.addEventListener('dblclick', (ev) => {
    const row = ev.target.closest('.pj4-row[data-id]');
    if (row && !ev.target.closest('.pj4-stage, .pj4-lead, .pj4-dots')) open(shown.find((x) => x.id === row.dataset.id));
  });

  // The preview reads two small things the list does not hold: the project's
  // instructions and its latest email. One read each, on selection, memoised.
  const extra = new Map();
  const extraFor = (p) => {
    if (!extra.has(p.id)) {
      extra.set(p.id, Promise.all([
        listInstructions(p.id).catch(() => null),
        p.number ? projectEmails(String(p.number), 5).catch(() => null) : Promise.resolve([]),
      ]).then(([ins, mails]) => ({ ins, mail: Array.isArray(mails) && mails.length ? newestFirst(mails)[0] : null })));
    }
    return extra.get(p.id);
  };
  const senderName = (from) => {
    const f = String(from || '');
    return (f.match(/^"?([^"<]+?)"?\s*</) || [, ''])[1].trim() || f.split('<')[0].trim() || f;
  };
  // "RE: TRACK// 8570 Conformity certificate — BC query" → "Conformity certificate — BC query".
  const shortSubject = (s, no) => String(s || '').replace(/^\s*((re|fw|fwd)\s*:\s*)+/i, '')
    .replace(/\b[A-Z]{2,}\/\/\s*/g, '').replace(new RegExp(`^\\s*${String(no || '').replace(/\W/g, '')}\\b\\s*`), '').trim() || '(no subject)';
  const dayMon = (v) => { const d = asDate(v) || new Date(v); return d && !isNaN(d) ? d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : ''; };

  function paintPanel() {
    const p = wide() && selId ? projects.find((x) => x.id === selId) : null;
    root.classList.toggle('has-panel', !!p);
    panel.hidden = !p;
    if (!p) { panel.innerHTML = ''; return; }
    const lead = leadNameOf(p);
    const leadFirst = lead.split(' ')[0];
    const scheme = schemeOf(p.scheme) ? titleCase(schemeOf(p.scheme)) : '';
    const late = isOverdue(p);
    const n = daysOver(p);
    const c = clientDoc[refTail(p.clientRef)] || null;
    const contact = (c && c.contactName) || p.contactName || '';
    const d = dueOf(p);
    const alert = late ? `<div class="alert-v4 pj4-pv-alert"><span class="alert-text"><b>${n} day${n === 1 ? '' : 's'} past its ${escapeHtml(ddmm(d))} deadline.</b> It is at ${escapeHtml(stageLabel(p.status))}${leadFirst ? ` with ${escapeHtml(leadFirst)}` : ''}. ${p.status === 'Review' ? 'Chase the check' : `Chase ${leadFirst ? escapeHtml(leadFirst) : 'the work'}`} or agree a new date with the client.</span></div>` : '';
    panel.innerHTML = `
      <div class="pj4-pv">
        <div class="pj4-pv-top"><span class="pj4-pv-no num-tech">${escapeHtml(p.number || '—')}</span>
          <button type="button" class="btn icon pj4-pv-x" data-pv="close" aria-label="Close the preview">${navIcon('close', 16)}</button></div>
        <h2 class="pj4-pv-client">${escapeHtml(p.clientName || '—')}</h2>
        <div class="pj4-pv-sub">${escapeHtml([p.title || p.service, scheme].filter(Boolean).join(' · '))}</div>
        <div class="pj4-pv-acts">
          <button type="button" class="btn primary" data-pv="open">Open project</button>
          <button type="button" class="btn" data-pv="time">Log time</button>
          ${leadFirst ? `<button type="button" class="btn" data-pv="ask">Ask ${escapeHtml(leadFirst)}</button>` : ''}
        </div>
        ${alert}
        <dl class="pj4-kv">
          <div><dt>Stage</dt><dd>${escapeHtml(stageLabel(p.status))}</dd></div>
          <div><dt>Lead</dt><dd>${escapeHtml(lead || 'Not assigned')}</dd></div>
          ${contact ? `<div><dt>Client contact</dt><dd>${escapeHtml(contact)}</dd></div>` : ''}
          <div data-kv="ins"><dt>Instructions</dt><dd class="pj4-wait">…</dd></div>
          <div data-kv="mail"><dt>Latest</dt><dd class="pj4-wait">…</dd></div>
        </dl>
      </div>`;
    const pid = p.id;
    extraFor(p).then(({ ins, mail }) => {
      if (selId !== pid || !panel.isConnected) return;
      const insEl = panel.querySelector('[data-kv="ins"] dd');
      if (insEl) {
        const list = Array.isArray(ins) ? ins.slice().sort((a, b) => (Number(a.seq) || 0) - (Number(b.seq) || 0)) : null;
        insEl.classList.remove('pj4-wait');
        insEl.textContent = !list ? 'Could not read' : list.length ? `${list.length} · ${list[0].description || 'Original scope'}` : 'None yet';
      }
      const mailRow = panel.querySelector('[data-kv="mail"]');
      if (mailRow) {
        if (!mail) mailRow.remove();
        else { const dd = mailRow.querySelector('dd'); dd.classList.remove('pj4-wait'); dd.textContent = `${senderName(mail.from)}: ${shortSubject(mail.subject, p.number)}${mail.date ? ` · ${dayMon(mail.date)}` : ''}`; }
      }
    });
  }
  panel.addEventListener('click', (ev) => {
    const b = ev.target.closest('[data-pv]'); if (!b) return;
    const p = projects.find((x) => x.id === selId);
    if (b.dataset.pv === 'close') { select(null); return; }
    if (!p) return;
    // "Ask {lead}" opens the project for now, as Home's "Ask {lead}" does — the
    // shared ask-a-colleague action (spec §1.7) is not built yet.
    if (b.dataset.pv === 'open' || b.dataset.pv === 'ask') open(p);
    // Log time hands over to the shell's dialog (track:log-time); without one, the Time screen.
    if (b.dataset.pv === 'time') requestLogTime({ projectId: p.id, projectNo: p.number || null }, () => goToView('time'));
  });

  // J / K move, Enter opens, Esc closes the preview. Never while typing, never
  // over a dialog or menu, never with a modifier (⌘K is the command bar).
  const onKey = (ev) => {
    if (!root.isConnected) { document.removeEventListener('keydown', onKey); return; }
    if (ev.metaKey || ev.ctrlKey || ev.altKey) return;
    if (document.querySelector('.mm, .tf-scrim, dialog[open], .inline-pick, .pj4-menu, .pmenu, .askbar-panel')) return;
    const a = document.activeElement;
    const typing = a && (a.tagName === 'INPUT' || a.tagName === 'TEXTAREA' || a.tagName === 'SELECT' || a.isContentEditable);
    const k = ev.key;
    if (typing && !(k === 'Enter' && a.id === 'search')) return;
    if (!shown.length) return;
    const ix = shown.findIndex((p) => p.id === selId);
    if (k === 'j' || k === 'J' || k === 'ArrowDown' && !typing) {
      ev.preventDefault();
      select(shown[Math.min(shown.length - 1, ix + 1)].id, { scroll: true });
    } else if (k === 'k' || k === 'K' || k === 'ArrowUp' && !typing) {
      ev.preventDefault();
      select(shown[Math.max(0, ix < 0 ? 0 : ix - 1)].id, { scroll: true });
    } else if (k === 'Enter' && ix >= 0) {
      if (!typing && a && a.closest && a.closest('button, a') && !a.closest('.pj4-row')) return;
      ev.preventDefault();
      open(shown[ix]);
    } else if (k === 'Escape' && selId && wide()) {
      select(null);
    }
  };
  document.addEventListener('keydown', onKey);
  // The panel appears or goes as the window crosses 1100px.
  let wasWide = wide();
  const onResize = () => {
    if (!root.isConnected) { window.removeEventListener('resize', onResize); return; }
    if (wide() !== wasWide) { wasWide = wide(); if (wasWide && !selId && shown.length) selId = shown[0].id; paintPanel(); }
  };
  window.addEventListener('resize', onResize);

  // ---- Meeting mode (v4.0.0, design 23/10/2026): views/meeting.js --------
  // The button opens it; while this person has a meeting going it reads
  // "Resume meeting", and a remote-office member gets "Follow the meeting"
  // beside it while a running meeting lets them. The Stage and lead writes are
  // THIS screen's own (applyStatus / applyLead), handed over so the meeting
  // changes a project exactly the way the list does.
  const meetBtn = mount.querySelector('#meeting');
  const meetingOpts = (extra = {}) => ({
    projects, users, tasks, session, applyStatus, applyLead,
    onClose: () => { draw(); paintPanel(); checkMeeting(); }, ...extra,
  });
  meetBtn.addEventListener('click', () => openMeeting(meetingOpts()));
  function checkMeeting() {
    if (!session) return;
    resumableMeeting(session).then((m) => { if (meetBtn.isConnected) meetBtn.textContent = m ? 'Resume meeting' : 'Meeting mode'; }).catch(() => {});
    followableMeeting(session, users).then((m) => {
      const acts = mount.querySelector('.pj4-head-acts');
      if (!acts) return;
      acts.querySelector('#meeting-follow')?.remove();
      if (!m) return;
      const b = document.createElement('button');
      b.id = 'meeting-follow'; b.type = 'button'; b.className = 'btn primary';
      b.textContent = `Follow the meeting · ${String(m.runnerName || '').split(' ')[0] || 'running'}`;
      b.addEventListener('click', () => openMeeting(meetingOpts({ follow: m })));
      acts.insertBefore(b, meetBtn);
    }).catch(() => {});
  }
  checkMeeting();

  draw();
}
